import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import Breadcrumbs from 'config/Breadcrumb';

import { Card, CardHeader, CardContent, Button, Divider, Typography, Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function ViewSecurityAlert() {
  
  // useLocation hook
  const location = useLocation();
  const alertId = location.state?.alertId;
  const name = location?.state?.name;
  const time = location?.state?.timestamp;
  
  // Redux Selectors
  const vulnerabilities = useSelector((state) => state.socData.vulnerabilities);
  const securityAlerts = useSelector((state) => state.socData.securityAlerts);
  const mitre = useSelector((state) => state.socData.mitre);
  const fimActivities = useSelector((state) => state.socData.fimActivities);
  const malware = useSelector((state) => state.socData.malwareActivities);

  // State Variables
  const [currentPath, setCurrentPath] = useState(['']);
  const [expand, setExpand] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  let module = ''

  // Use the appropriate selector based on the `name` value
  let alerts;



  switch (name) {
    case 'vulnerability':
      alerts = vulnerabilities;
      module = 'Vulnerability Management'
      break;
    case 'securityAlert':
      alerts = securityAlerts;
      module = 'Threat Hunting'
      break;
    case 'mitre':
      alerts = mitre;
      module = 'Mitre Attack'
      break;
    case 'fim':
      alerts = fimActivities;
      module = 'File Integrity Monitoring'
      break;
    case 'malware':
      alerts = malware;
      module = 'Malicious Activities'
      break;
    default:
      alerts = null;  // Default case in case name doesn't match any of the expected values
      break;
  }

  const selectedAlert = alerts?.data?.find(
    (alert) => alert._id === alertId
  );

  const fullLog = selectedAlert._source?.full_log || '';
  const truncatedLog = fullLog.length > 200 ? fullLog.slice(0, 200) + '...' : fullLog;

  useEffect(() => {
    setCurrentPath([module, selectedAlert?._source?.agent?.name, 'View Alert']);
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId])

  // Handles to format the property name
  const formatPropertyName = (key) => {
    if (key === 'level') return 'Severity';
    else if (key === 'ip' || key === 'id') return key.toUpperCase();
    else if (key === 'cve') return 'CVE'
    else if (key === 'cvss3') return 'CVSS3 Score'
    else
      return key.charAt(0).toUpperCase() + key.slice(1)
  };

  // Handles to get the proeprty names 
  const renderKey = (key, value, parentKey = "") => {
    const formattedKey = formatPropertyName(key); // Assuming this function formats the key properly.
    const fullKey = parentKey ? `${parentKey}-${formattedKey}` : `data-${formattedKey}`;

    // If value is an object, recursively generate the keys for nested objects
    if (typeof value === "object" && value !== null) {
      return Object.entries(value).map(([nestedKey, nestedValue], index) => (
        <div key={`nested-${index}`} className="word-wrap">
          {renderKey(nestedKey, nestedValue, fullKey)} 
        </div>
      ));
    }

    // If value is not an object, just return the formatted key
    return fullKey;
  };

  // Handles to set the colors based on severity level
  const getSeverityColor = (severity) => {
    if (severity >= 0 && severity <= 6) return '#4baeec'; // Low
    if (severity >= 7 && severity <= 8) return '#4e4ece'; // Medium
    if (severity >= 9 && severity <= 11) return '#fc659c'; // High
    if (severity >= 12) return 'red'; // Critical
    return 'black'; // Default color
  };

  // Handles to get parent property value 
  const renderTTPsValue = (key, value) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'object') {
      return <pre>{JSON.stringify(value, null, 2)}</pre>;
    }
    if (key === 'level') {
      const severityLabel =
        value >= 0 && value <= 6
          ? 'Low'
          : value >= 7 && value <= 8
            ? 'Medium'
            : value >= 9 && value <= 11
              ? 'High'
              : 'Critical';

      return (
        <span style={{ color: getSeverityColor(value), fontWeight: 'bold' }}>
          {severityLabel}
        </span>
      );
    }
    return value || 'N/A';
  };

  // handles to get the property value form nested objects or arrays
  const renderValue = (value) => {
    // If it's an array, join elements with commas
    if (Array.isArray(value)) {
      return value.join(', '); // Join the array values by comma
    }

    // If it's an object, recursively render its properties
    if (typeof value === 'object' && value !== null) {
      return Object.entries(value).map(([nestedKey, nestedValue], idx) => (
        <div key={`nested-${idx}`}>
          {renderValue(nestedValue)}
        </div>
      ));
    }

    return value;
  };

  // Handles to render the nested objects
  const renderNestedObject = (obj) => {
    return Object.keys(obj).map((nestedKey, nestedIndex) => {
      const nestedValue = obj[nestedKey];
      if (typeof nestedValue === 'object' && nestedValue !== null) {
        if (nestedKey === 'message')
          return (
            <div key={`nested-object-${nestedIndex}`} className='ps-4'>
              <strong>{formatPropertyName(nestedKey)}:</strong>
              <div>{nestedValue.substring(0, 10)}</div>
            </div>
          );
        else
          return (
            <div key={`nested-object-${nestedIndex}`} className='ps-4'>
              <strong>{formatPropertyName(nestedKey)}:</strong>
              <div>{renderNestedObject(nestedValue)}</div>
            </div>
          );
      }

      // If it's not an object, render it as a key-value pair
      return (
        <div className="row" key={`nested-${nestedIndex}`}>
          <div className="col-4 text-body1">
            <strong className="word-wrap">{formatPropertyName(nestedKey)}</strong>:
          </div>
          <div className="col-8 text-body1 text-grey-8">
            {renderTTPsValue(nestedKey, nestedValue)}
          </div>
        </div>
      );
    });
  };

  return (
    <div>
      <Card>
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <Breadcrumbs paths={currentPath} />
                  </label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="back font"
                    onClick={() => window.history.back()}
                  >
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent>
          {selectedAlert ? (
            <div className={`d-flex ${selectedAlert._source?.rule?.mitre ? 'justify-content-between' : 'justify-content-center'}`}
            >
              <Card style={{ width: '65%' }}>
                <CardHeader title={`${selectedAlert?._source?.rule?.description}`}
                  titleTypographyProps={{
                    style: { color: '#0E0A65', fontWeight: 'bold' },
                  }}
                />
                <CardContent>
                  {/* Agent Details */}
                  <div className="row">
                    <div className="col-4 text-body1">
                      <strong className="word-wrap">
                        Alert Occurred On:
                      </strong>
                    </div>
                    <div className="col-8 text-body1 text-grey-8">
                      {time}
                    </div>
                  </div>
                  {selectedAlert._source?.agent &&
                    Object.keys(selectedAlert._source.agent)
                      .filter((key) => key !== 'id' && key !== 'labels')
                      .map((key, index) => (
                        <div className="row" key={`agent-${index}`}>
                          <div className="col-4 text-body1">
                            <strong className="word-wrap">
                              {formatPropertyName(key)}
                            </strong>
                            :
                          </div>
                          <div className="col-8 text-body1 text-grey-8">
                            {renderTTPsValue(key, selectedAlert._source?.agent[key])}
                          </div>
                        </div>
                      ))}

                  {/* Rule Details */}
                  {selectedAlert._source?.rule &&
                    Object.keys(selectedAlert._source.rule)
                      .filter((key) =>
                        name !== 'vulnerability'
                          ? key !== 'mitre' && key === 'level' && key !== 'mail' && key !== 'description' && key !== 'firedtimes' && key !== 'groups' && key !== 'pci_dss' && key !== "hipaa" && key !== "tsc" && key !== "id" && key !== "nist_800_53" && key !== "gdpr"
                          : key !== 'mitre' && key === 'level' && key !== 'mail' && key !== 'description' && key !== 'firedtimes' && key !== 'groups' && key !== 'pci_dss' && key !== "hipaa" && key !== "tsc" && key !== "id" && key !== "nist_800_53" && key !== "gdpr"
                      )
                      .map((key, index) => (
                        <div className="row" key={`rule-${index}`}>
                          <div className="col-4 text-body1">
                            <strong className="word-wrap">
                              {formatPropertyName(key)}
                            </strong>
                            :
                          </div>
                          <div className="col-8 text-body1 text-grey-8">
                            {renderTTPsValue(key, selectedAlert._source?.rule[key])}
                          </div>
                        </div>
                      ))}
                  {selectedAlert._source?.location && (
                    <div className="row">
                      <div className="col-4 text-body1">
                        <strong className="word-wrap">
                          Location
                        </strong>
                        :
                      </div>
                      <div className="col-8 text-body1 text-grey-8">
                        {selectedAlert._source?.location}
                      </div>
                    </div>
                  )}


                  {selectedAlert?._source?.data?.vulnerability && (
                    <div className="my-2">
                      <Accordion className='mx-2'>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span"><b>Vulnerability Data</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {selectedAlert?._source?.data?.vulnerability &&
                            Object.keys(selectedAlert._source.data.vulnerability)
                              .filter((key) => key !== 'package' && key !== 'cvss' && key !== 'score' && key !== 'severity').filter(([key]) => key !== 'score')
                              .map((key, index) => (
                                <div className="row" key={`vulnerability-${index}`}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8 word-wrap">
                                    {renderTTPsValue(key, selectedAlert._source?.data?.vulnerability[key])}
                                  </div>
                                </div>
                              ))}
                          {selectedAlert._source?.data?.vulnerability?.package &&
                            Object.keys(selectedAlert._source.data.vulnerability?.package)
                              .map((key, index) => (
                                <div className="row" key={`vulnerability-${index}`}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8">
                                    {renderTTPsValue(key, selectedAlert._source?.data?.vulnerability?.package[key])}
                                  </div>
                                </div>
                              ))}
                          {selectedAlert._source?.data?.vulnerability?.package &&
                            Object.keys(selectedAlert._source.data.vulnerability?.cvss)
                              .map((key, index) => (
                                <div className="row" key={`vulnerability-${index}`}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8">
                                    {renderTTPsValue(key, selectedAlert._source?.data?.vulnerability?.cvss[key]?.base_score)}
                                  </div>
                                </div>
                              ))}
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}

                  {selectedAlert._source?.data?.win?.eventdata && (
                    <div className="my-2">
                      <Accordion className='mx-2' defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span"><b>Event Data</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {selectedAlert._source?.data?.win?.eventdata &&
                            Object.entries(selectedAlert._source.data.win.eventdata).map(
                              ([key, value], index) => (
                                <div className="row" key={`eventdata-${index}`}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8 word-wrap">
                                    {renderTTPsValue(key, value)}
                                  </div>
                                </div>
                              )
                            )}
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}
                  {selectedAlert._source?.data?.win?.system && (
                    <div className='my-2'>
                      <Accordion className='mx-2'>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span"><b>System Data</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {selectedAlert._source?.data?.win?.system &&
                            Object.entries(selectedAlert._source.data.win.system).filter(([key]) => key !== 'message' && key !== 'level').map(
                              ([key, value], index) => (
                                <div className="row" key={`system-${index}`}>
                                  <div className="col-4 text-body1">
                                    <strong className="word-wrap">
                                      {formatPropertyName(key)}
                                    </strong>
                                    :
                                  </div>
                                  <div className="col-8 text-body1 text-grey-8 word-wrap">
                                    {renderTTPsValue(key, value)}
                                  </div>
                                </div>
                              )
                            )}
                          {selectedAlert._source?.data?.win?.system?.message && (
                            <div className="row">
                              <div className="col-4 text-body1">
                                <strong className="word-wrap">
                                  Message
                                </strong>
                                :
                              </div>
                              <div className="col-8 text-body1 text-grey-8">
                                {selectedAlert._source?.data?.win?.system?.message?.length > 250 && !expand ? (
                                  <p className='col'>
                                    {selectedAlert._source?.data?.win?.system?.message?.slice(0, 250)}...
                                    <span
                                      onClick={() => { setExpand(!expand) }}
                                     className='expand-more'
                                    >
                                      Read more
                                    </span>
                                  </p>) : (
                                  <p className='col'>{selectedAlert._source?.data?.win?.system?.message}</p>)}
                              </div>
                            </div>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}


                  {selectedAlert._source?.syscheck && (
                    <div className="my-2">
                      <Accordion className='mx-2' defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span"><b>Syscheck Data</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {selectedAlert._source?.syscheck &&
                            Object.entries(selectedAlert._source.syscheck).map(([key, value], index) => (
                              <div className="row" key={`eventdata-${index}`}>
                                <div className="col-4 text-body1">
                                  <strong className="word-wrap">
                                    {formatPropertyName(key)}
                                  </strong>
                                  :
                                </div>
                                <div className="col-8 text-body1 text-grey-8 word-wrap">
                                  {renderValue(value)} {/* Render the value based on its type */}
                                </div>
                              </div>
                            ))
                          }
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}


                  {selectedAlert._source.data && selectedAlert._source.data.win === undefined && selectedAlert._source.data.vulnerability === undefined && (
                    <div className="my-2">
                      <Accordion className='mx-2' defaultExpanded>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="panel1-content"
                          id="panel1-header"
                        >
                          <Typography component="span"><b>Data</b></Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {selectedAlert._source?.data &&
                            Object.entries(selectedAlert._source.data).map(([key, value], index) => (
                              <div className="row" key={`eventdata-${index}`}>
                                <div className="col-4 text-body1">
                                  <strong className="word-wrap">
                                    {renderKey(key, value)}
                                  </strong>
                                  :
                                </div>
                                <div className="col-8 text-body1 text-grey-8 word-wrap">
                                  {renderValue(value)}
                                </div>
                              </div>
                            ))
                          }
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}


                  {fullLog && (
                    <div className="row">
                      <div className="col-4 text-body1">
                        <strong className="word-wrap">Full Log</strong>:
                      </div>
                      <div className="col-8 text-body1 text-grey-8 word-wrap">
                        {isExpanded ? fullLog : truncatedLog}
                        {fullLog.length > 200 && (
                          <span onClick={() => setIsExpanded(!isExpanded)} className='expand-more'>
                            {isExpanded ? 'Read Less' : 'Read More'}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Mitre Details */}
              {selectedAlert?._source?.rule?.mitre && (
                <Card style={{ width: '30%', height: '30%' }}>
                  <CardHeader title="Mitre Details"
                    titleTypographyProps={{
                      style: { color: '#0E0A65', fontWeight: 'bold' },
                    }}
                  />
                  <CardContent>
                    {selectedAlert._source?.rule?.mitre &&
                      Object.keys(selectedAlert._source.rule?.mitre).map((key, index) => (
                        <div className="row" key={`mitre-${index}`}>
                          <div className="col-4 text-body1">
                            <strong className="word-wrap">
                              {formatPropertyName(key)}
                            </strong>
                            :
                          </div>
                          <div className="col-8 text-body1 text-grey-8">
                            {renderTTPsValue(key, selectedAlert._source?.rule?.mitre[key])}
                          </div>
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <p>Loading alert details...</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ViewSecurityAlert;
